import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight, Package, Truck, Home, Check, Building2, Users, ExternalLink, Bed, ShoppingCart, ThumbsUp, Star, Quote, X, Sparkles, Clock, Leaf, Wallet, Luggage } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const REVIEWS = [
  {
    id: 1,
    name: "Marie L.",
    location: "Saint-Malo",
    rating: 5,
    text: "Service impeccable ! Le linge était déjà dans notre location à notre arrivée. Draps très doux et bien préparés. Je recommande vivement !",
    date: "Août 2025"
  },
  {
    id: 2,
    name: "Philippe D.",
    location: "Dinard",
    rating: 5,
    text: "Nous avons loué pour notre gîte pendant toute la saison. Un vrai gain de temps ! Plus besoin de gérer les lessives entre deux locations.",
    date: "Juillet 2025"
  },
  {
    id: 3,
    name: "Sophie M.",
    location: "Cancale",
    rating: 5,
    text: "Très pratique pour nos vacances en famille. Les serviettes étaient moelleuses et sentaient bon le propre. Service très réactif par email.",
    date: "Septembre 2025"
  },
  {
    id: 4,
    name: "Jean-Pierre B.",
    location: "Perros-Guirec",
    rating: 5,
    text: "Propriétaire de 3 locations, je ne pourrais plus m'en passer ! La qualité est constante et mes voyageurs sont ravis.",
    date: "Juin 2025"
  },
  {
    id: 5,
    name: "Isabelle R.",
    location: "Quiberon",
    rating: 5,
    text: "Première utilisation et conquise ! Commande simple, livraison ponctuelle. Le linge était parfaitement plié et emballé.",
    date: "Août 2025"
  },
  {
    id: 6,
    name: "Thomas G.",
    location: "Carnac",
    rating: 5,
    text: "Enfin un service qui nous simplifie la vie ! En tant que conciergerie, c'est un partenaire idéal. Rapport qualité/prix excellent.",
    date: "Juillet 2025"
  },
  {
    id: 7,
    name: "Catherine P.",
    location: "Vannes",
    rating: 5,
    text: "Les draps sont de très belle qualité. Mes locataires ont tous laissé des commentaires positifs. Merci Terre et Mer !",
    date: "Septembre 2025"
  },
  {
    id: 8,
    name: "Laurent M.",
    location: "Saint-Brieuc",
    rating: 5,
    text: "Service au top ! Réactivité, qualité, simplicité. Tout ce qu'on attend d'un prestataire. Je recommande les yeux fermés.",
    date: "Août 2025"
  }
];

const HomePage = () => {
  const navigate = useNavigate();
  const [postalCode, setPostalCode] = useState('');
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (postalCode) params.set('location', postalCode);
    if (arrivalDate) params.set('arrival', format(arrivalDate, 'yyyy-MM-dd'));
    if (departureDate) params.set('departure', format(departureDate, 'yyyy-MM-dd'));
    navigate(`/products?${params.toString()}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_wash-easy-br/artifacts/pd7c43z5_iStock-905186016.jpg')`
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
          <div className="max-w-3xl">
            <span className="inline-block text-sm uppercase tracking-[0.2em] text-white/80 mb-4 animate-fade-in-up">
              Terre et Mer • Location de Linge
            </span>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-white mb-6 animate-fade-in-up stagger-1">
              Du linge de qualité pour vos séjours en Bretagne
            </h1>
            <p className="text-lg text-white/80 mb-10 max-w-xl animate-fade-in-up stagger-2">
              Draps, serviettes, linge de maison... Commandez en ligne, nous livrons directement dans votre logement.
            </p>
          </div>

          {/* Booking Form */}
          <div className="bg-white rounded-xl shadow-xl p-6 md:p-8 max-w-4xl animate-fade-in-up stagger-3" data-testid="booking-form">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Location Input */}
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-[#64748B] mb-2">
                  Lieu de livraison
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#64748B]" />
                  <Input
                    type="text"
                    placeholder="Code postal"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className="pl-10 h-12 rounded-lg border-gray-200"
                    data-testid="location-input"
                  />
                </div>
              </div>

              {/* Arrival Date */}
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-[#64748B] mb-2">
                  Date d'arrivée
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-start text-left font-normal rounded-lg border-gray-200"
                      data-testid="arrival-date-btn"
                    >
                      <Calendar className="mr-2 h-4 w-4 text-[#64748B]" />
                      {arrivalDate ? format(arrivalDate, 'dd MMM yyyy', { locale: fr }) : 'Sélectionner'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={arrivalDate}
                      onSelect={setArrivalDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Departure Date */}
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-[#64748B] mb-2">
                  Date de départ
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-start text-left font-normal rounded-lg border-gray-200"
                      data-testid="departure-date-btn"
                    >
                      <Calendar className="mr-2 h-4 w-4 text-[#64748B]" />
                      {departureDate ? format(departureDate, 'dd MMM yyyy', { locale: fr }) : 'Sélectionner'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={departureDate}
                      onSelect={setDepartureDate}
                      disabled={(date) => date < (arrivalDate || new Date())}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Search Button */}
              <div className="md:col-span-1 flex items-end">
                <Button
                  className="w-full h-12 bg-[#E8945A] hover:bg-[#D4844A] rounded-lg text-base text-white"
                  onClick={handleSearch}
                  data-testid="booking-form-submit"
                >
                  Voir les produits
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-[#2D3E50] py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-white">
            <div className="flex items-center justify-center gap-3">
              <Truck className="h-6 w-6 text-[#E8945A]" />
              <span className="text-sm font-medium">Dépôt directement dans le logement</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Calendar className="h-6 w-6 text-[#E8945A]" />
              <span className="text-sm font-medium">Commandez 3 jours avant votre arrivée (coupure 16h)</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Package className="h-6 w-6 text-[#E8945A]" />
              <span className="text-sm font-medium">Service sans engagement</span>
            </div>
          </div>
        </div>
      </section>

      {/* C'est... Section with Image */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div>
              <div className="mb-8">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-['Playfair_Display'] font-semibold text-2xl text-[#2D3E50]">TERRE</span>
                  <span className="font-['Playfair_Display'] font-normal text-2xl text-[#E8945A] italic">et</span>
                  <span className="font-['Playfair_Display'] font-semibold text-2xl text-[#2D3E50]">MER</span>
                  <span className="text-sm uppercase tracking-wider text-[#64748B] ml-2">Location de Linge</span>
                </div>
                <p className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#2D3E50] italic">c'est ...</p>
              </div>

              <p className="text-lg text-[#64748B] mb-10 leading-relaxed">
                Le <span className="font-semibold text-[#2D3E50]">premier service</span> de location et livraison de linge pour les locations saisonnières, pour les particuliers et les professionnels.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2D3E50]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bed className="h-6 w-6 text-[#2D3E50]" />
                  </div>
                  <div>
                    <p className="text-[#2D3E50]">
                      Du <span className="font-semibold">linge de maison</span> lavé et préparé en <span className="font-semibold text-[#E8945A]">atelier professionnel</span>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2D3E50]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="h-6 w-6 text-[#2D3E50]" />
                  </div>
                  <div>
                    <p className="text-[#2D3E50]">
                      Des commandes rapides <span className="font-semibold text-[#E8945A]">100% en ligne</span>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2D3E50]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <ThumbsUp className="h-6 w-6 text-[#2D3E50]" />
                  </div>
                  <div>
                    <p className="text-[#2D3E50]">
                      Un service <span className="font-semibold">simple</span> et <span className="font-semibold text-[#E8945A]">sans engagement</span>
                    </p>
                  </div>
                </div>
              </div>

              <Button
                size="lg"
                className="mt-10 bg-[#E8945A] hover:bg-[#D4844A] rounded-lg h-14 px-8"
                onClick={() => navigate('/products')}
              >
                Découvrir nos produits
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Right - Atelier pro photo (équipe TERRE et MER en blanchisserie) */}
            <div className="relative" data-testid="brand-visual">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-square bg-[#F5F1E8]">
                <img
                  src="https://customer-assets.emergentagent.com/job_wash-easy-br/artifacts/gaetg3ov_ChatGPT%20Image%2010%20mai%202026%2C%2014_03_16.png"
                  alt="Notre équipe TERRE et MER prépare votre linge dans notre atelier de blanchisserie professionnelle"
                  className="w-full h-full object-cover"
                  data-testid="brand-hero-image"
                />
                {/* Soft top-bottom gradient for legibility of overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#112241]/55 via-transparent to-[#112241]/15 pointer-events-none" />

                {/* Top-left caption chip */}
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E8945A]" />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#2D3E50] font-medium">
                    Notre atelier · Dinan (22)
                  </span>
                </div>

                {/* Bottom overlay card */}
                <div className="absolute bottom-5 left-5 right-5 rounded-xl bg-white/95 backdrop-blur-md p-5 shadow-2xl border border-white/40">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-3.5 w-3.5 fill-[#E8945A] text-[#E8945A]" />
                      ))}
                    </div>
                    <span className="text-[11px] uppercase tracking-wider text-[#64748B]">
                      Lavage professionnel
                    </span>
                  </div>
                  <p className="font-['Playfair_Display'] text-lg md:text-xl text-[#2D3E50] leading-snug italic">
                    « Lavé, plié, contrôlé — puis livré chez vous. »
                  </p>
                </div>
              </div>

              {/* Floating "delivered" badge */}
              <div className="hidden md:flex absolute -bottom-5 -right-5 items-center gap-2 bg-[#2D3E50] text-white rounded-full shadow-xl px-5 py-3">
                <Truck className="h-4 w-4 text-[#E8945A]" />
                <span className="text-sm font-medium">
                  Livré chez vous
                </span>
              </div>

              {/* Floating "atelier pro" badge top-right */}
              <div className="hidden md:flex absolute -top-5 -right-5 items-center gap-2 bg-[#E8945A] text-white rounded-full shadow-xl px-5 py-3">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Atelier pro
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 md:py-32 px-6 md:px-12 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.2em] text-[#E8945A] font-medium">
              Nos Services
            </span>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#2D3E50] mt-4">
              Une solution pour chaque besoin
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Propriétaires Card */}
            <div className="service-card bg-white rounded-2xl border border-gray-100 p-8 md:p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#FFF5ED] rounded-xl flex items-center justify-center">
                  <Users className="h-7 w-7 text-[#E8945A]" />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#2D3E50]">
                    Terre et Mer Propriétaire
                  </h3>
                  <p className="text-sm text-[#64748B]">Pour les particuliers</p>
                </div>
              </div>

              <p className="text-[#64748B] mb-6 leading-relaxed">
                Vous êtes propriétaire d'une location saisonnière ? 
                <span className="text-[#2D3E50] font-medium"> Terre et Mer Linge </span>
                propose un service simple, de qualité et sans engagement pour vous débarrasser des corvées de linge.
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-[#2D3E50]">
                  <Check className="h-5 w-5 text-[#E8945A] flex-shrink-0" />
                  <span>Gîtes et maisons d'hôtes</span>
                </li>
                <li className="flex items-center gap-3 text-[#2D3E50]">
                  <Check className="h-5 w-5 text-[#E8945A] flex-shrink-0" />
                  <span>Locations courtes durées</span>
                </li>
                <li className="flex items-center gap-3 text-[#2D3E50]">
                  <Check className="h-5 w-5 text-[#E8945A] flex-shrink-0" />
                  <span>Maisons de vacances</span>
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  className="bg-[#2D3E50] hover:bg-[#3D4F61] rounded-lg"
                  onClick={() => navigate('/products')}
                >
                  Découvrir nos offres
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="border-[#E8945A] text-[#E8945A] hover:bg-[#FFF5ED] rounded-lg"
                  onClick={() => window.open('https://terreetmer-conciergerie.fr/', '_blank')}
                >
                  Notre conciergerie
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Professionnels Card */}
            <div className="service-card bg-white rounded-2xl border border-gray-100 p-8 md:p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#E8F4FC] rounded-xl flex items-center justify-center">
                  <Building2 className="h-7 w-7 text-[#2D3E50]" />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#2D3E50]">
                    Terre et Mer Professionnel
                  </h3>
                  <p className="text-sm text-[#64748B]">Pour les entreprises</p>
                </div>
              </div>

              <p className="text-[#64748B] mb-6 leading-relaxed">
                Vous êtes professionnel de la location saisonnière ? 
                <span className="text-[#2D3E50] font-medium"> Terre et Mer Linge </span>
                propose des solutions simples, efficaces et rapides à mettre en place pour le linge de votre parc immobilier.
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-[#2D3E50]">
                  <Check className="h-5 w-5 text-[#2D3E50] flex-shrink-0" />
                  <span>Agences immobilières</span>
                </li>
                <li className="flex items-center gap-3 text-[#2D3E50]">
                  <Check className="h-5 w-5 text-[#2D3E50] flex-shrink-0" />
                  <span>Conciergeries</span>
                </li>
                <li className="flex items-center gap-3 text-[#2D3E50]">
                  <Check className="h-5 w-5 text-[#2D3E50] flex-shrink-0" />
                  <span>Campings et résidences</span>
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  className="bg-[#E8945A] hover:bg-[#D4844A] rounded-lg"
                  onClick={() => navigate('/products')}
                >
                  Voir nos tarifs
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="border-[#2D3E50] text-[#2D3E50] hover:bg-[#F5F5F5] rounded-lg"
                  onClick={() => window.open('https://terreetmer-conciergerie.fr/', '_blank')}
                >
                  Notre conciergerie
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparatif - Pourquoi louer chez nous ? */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.2em] text-[#E8945A] font-medium">
              Le bon choix pour vos vacances
            </span>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#2D3E50] mt-4 max-w-3xl mx-auto">
              Pourquoi louer votre linge plutôt que de l'apporter ?
            </h2>
            <p className="text-[#64748B] mt-4 max-w-2xl mx-auto">
              On compare honnêtement les deux options pour vous aider à choisir.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* APPORTER SON LINGE - Negative card */}
            <div
              className="relative rounded-2xl border-2 border-gray-200 bg-[#FAFAFA] p-8 md:p-10"
              data-testid="comparison-bring-own"
            >
              <div className="absolute -top-3 left-8 inline-flex items-center gap-2 bg-gray-200 text-[#64748B] px-4 py-1 rounded-full text-xs uppercase tracking-wider font-medium">
                Apporter son linge
              </div>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center">
                  <Luggage className="h-6 w-6 text-[#64748B]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-medium text-[#64748B]">
                  L'option « traditionnelle »
                </h3>
              </div>

              <ul className="space-y-4">
                {[
                  { title: 'Valises encombrées', desc: 'Draps et serviettes qui prennent toute la place dans le coffre.' },
                  { title: 'Lessives à gérer', desc: 'Linge sale à ramener, à laver et à repasser au retour.' },
                  { title: 'Poids et logistique', desc: 'Plusieurs kilos en plus, surtout pour les familles ou longs séjours.' },
                  { title: 'Qualité variable', desc: 'Linge personnel souvent moins épais et confortable que celui d\'un service professionnel.' },
                  { title: 'Temps perdu', desc: 'Préparer, plier, ranger… avant et après les vacances.' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`con-${i}`}>
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <X className="h-3.5 w-3.5 text-[#64748B]" strokeWidth={3} />
                    </div>
                    <div>
                      <p className="font-medium text-[#2D3E50]">{item.title}</p>
                      <p className="text-sm text-[#64748B] mt-0.5">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-[#64748B]">Verdict</span>
                <span className="text-sm font-medium text-[#64748B]">Stress &amp; corvées</span>
              </div>
            </div>

            {/* LOUER CHEZ NOUS - Positive card */}
            <div
              className="relative rounded-2xl border-2 border-[#E8945A] bg-gradient-to-br from-[#FFFBF5] to-white p-8 md:p-10 shadow-xl shadow-[#E8945A]/10"
              data-testid="comparison-rent-us"
            >
              <div className="absolute -top-3 left-8 inline-flex items-center gap-2 bg-[#E8945A] text-white px-4 py-1 rounded-full text-xs uppercase tracking-wider font-medium">
                <Sparkles className="h-3 w-3" />
                Louer chez Terre et Mer
              </div>
              <div className="absolute top-6 right-6 inline-flex items-center gap-1 bg-[#2D3E50] text-white px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium">
                Recommandé
              </div>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#FFF5ED] rounded-xl flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-[#E8945A]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-medium text-[#2D3E50]">
                  La solution sereine
                </h3>
              </div>

              <ul className="space-y-4">
                {[
                  { icon: Luggage, title: 'Voyagez léger', desc: 'Plus de draps dans la valise. Place aux affaires de plage et aux enfants.' },
                  { icon: Clock, title: 'Zéro lessive', desc: 'On dépose, vous profitez, on récupère. C\'est tout.' },
                  { icon: Star, title: 'Lavé pro', desc: 'Polyester recyclé ou coton 100%, lavé en blanchisserie professionnelle.' },
                  { icon: Wallet, title: 'Tarifs transparents', desc: 'À partir de 5€. Sans engagement, vous payez ce que vous utilisez.' },
                  { icon: Leaf, title: 'Plus écologique', desc: 'Lavage industriel mutualisé : moins d\'eau et d\'énergie qu\'à la maison.' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <li key={i} className="flex items-start gap-3" data-testid={`pro-${i}`}>
                      <div className="w-6 h-6 rounded-full bg-[#E8945A] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-[#E8945A]" />
                          <p className="font-medium text-[#2D3E50]">{item.title}</p>
                        </div>
                        <p className="text-sm text-[#64748B] mt-0.5">{item.desc}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-8 pt-6 border-t border-[#E8945A]/20 flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-[#E8945A] font-semibold">Verdict</span>
                <span className="text-sm font-semibold text-[#2D3E50]">Vacances 100% détente</span>
              </div>

              <Button
                className="w-full mt-6 bg-[#E8945A] hover:bg-[#D4844A] rounded-lg h-12 text-base"
                onClick={() => navigate('/products')}
                data-testid="comparison-cta"
              >
                Réserver mon linge dès maintenant
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Bottom argument bar */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center p-4 rounded-xl bg-[#FAFAFA]">
              <div className="font-['Playfair_Display'] text-3xl text-[#E8945A] font-medium">-30%</div>
              <div className="text-xs uppercase tracking-wider text-[#64748B] mt-1">de poids en valise</div>
            </div>
            <div className="text-center p-4 rounded-xl bg-[#FAFAFA]">
              <div className="font-['Playfair_Display'] text-3xl text-[#E8945A] font-medium">+2h</div>
              <div className="text-xs uppercase tracking-wider text-[#64748B] mt-1">gagnées par séjour</div>
            </div>
            <div className="text-center p-4 rounded-xl bg-[#FAFAFA]">
              <div className="font-['Playfair_Display'] text-3xl text-[#E8945A] font-medium">0</div>
              <div className="text-xs uppercase tracking-wider text-[#64748B] mt-1">lessive au retour</div>
            </div>
            <div className="text-center p-4 rounded-xl bg-[#FAFAFA]">
              <div className="font-['Playfair_Display'] text-3xl text-[#E8945A] font-medium">5★</div>
              <div className="text-xs uppercase tracking-wider text-[#64748B] mt-1">avis clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section id="comment-ca-marche" className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.2em] text-[#E8945A] font-medium">
              Simple et rapide
            </span>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#2D3E50] mt-4">
              Comment ça marche ?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Step 1 */}
            <div className="text-center" data-testid="step-1">
              <div className="w-20 h-20 bg-[#FFF5ED] rounded-full flex items-center justify-center mx-auto mb-6">
                <Package className="h-10 w-10 text-[#E8945A]" />
              </div>
              <div className="text-4xl font-['Playfair_Display'] font-bold text-[#E8945A]/30 mb-2">01</div>
              <h3 className="font-['Playfair_Display'] text-xl font-medium text-[#2D3E50] mb-3">
                Commandez en ligne
              </h3>
              <p className="text-[#64748B]">
                Sélectionnez votre linge et indiquez vos dates de séjour. Livraison gratuite si commande passée 3 jours avant l'arrivée (coupure quotidienne 16h).
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center" data-testid="step-2">
              <div className="w-20 h-20 bg-[#FFF5ED] rounded-full flex items-center justify-center mx-auto mb-6">
                <Truck className="h-10 w-10 text-[#E8945A]" />
              </div>
              <div className="text-4xl font-['Playfair_Display'] font-bold text-[#E8945A]/30 mb-2">02</div>
              <h3 className="font-['Playfair_Display'] text-xl font-medium text-[#2D3E50] mb-3">
                Livraison dans le logement
              </h3>
              <p className="text-[#64748B]">
                Nous préparons et livrons votre commande avant votre arrivée prévue à 16h.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center" data-testid="step-3">
              <div className="w-20 h-20 bg-[#FFF5ED] rounded-full flex items-center justify-center mx-auto mb-6">
                <Home className="h-10 w-10 text-[#E8945A]" />
              </div>
              <div className="text-4xl font-['Playfair_Display'] font-bold text-[#E8945A]/30 mb-2">03</div>
              <h3 className="font-['Playfair_Display'] text-xl font-medium text-[#2D3E50] mb-3">
                Profitez de votre séjour
              </h3>
              <p className="text-[#64748B]">
                Tout est prêt ! À votre départ, laissez le linge sur place, nous récupérons tout.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              className="bg-[#2D3E50] hover:bg-[#3D4F61] rounded-lg h-14 px-8 text-base"
              onClick={() => navigate('/products')}
              data-testid="cta-commander"
            >
              Voir tous nos produits
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.2em] text-[#E8945A] font-medium">
              Témoignages
            </span>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#2D3E50] mt-4">
              Un service de qualité
            </h2>
            <p className="text-[#64748B] mt-4 max-w-xl mx-auto">
              Découvrez les avis de nos clients satisfaits
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {REVIEWS.map((review) => (
              <div 
                key={review.id}
                className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-[#E8945A] text-[#E8945A]" />
                  ))}
                </div>
                <div className="relative mb-4">
                  <Quote className="absolute -top-2 -left-2 h-8 w-8 text-[#E8945A]/20" />
                  <p className="text-[#64748B] text-sm leading-relaxed pl-4">
                    {review.text}
                  </p>
                </div>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-semibold text-[#2D3E50]">{review.name}</p>
                  <p className="text-sm text-[#64748B]">{review.location} • {review.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_wash-easy-br/artifacts/7n8qhsyn_iStock-935541700.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-[#2D3E50]/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-sm uppercase tracking-[0.2em] text-[#E8945A] font-medium">
            Terre et Mer Conciergerie
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mt-4 mb-6 max-w-2xl mx-auto">
            Besoin d'un accompagnement complet pour votre location ?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Découvrez notre service de conciergerie pour les propriétaires en Bretagne.
          </p>
          <Button
            size="lg"
            className="bg-[#E8945A] hover:bg-[#D4844A] text-white rounded-lg h-14 px-8"
            onClick={() => window.open('https://terreetmer-conciergerie.fr/', '_blank')}
          >
            Découvrir Terre et Mer Conciergerie
            <ExternalLink className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.2em] text-[#E8945A] font-medium">
              Questions fréquentes
            </span>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#2D3E50] mt-4">
              Besoin d'aide ?
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            <AccordionItem value="item-1" className="bg-[#FAFAFA] rounded-xl border border-gray-100 px-6">
              <AccordionTrigger className="text-left font-['Playfair_Display'] font-medium text-[#2D3E50] hover:text-[#E8945A] py-5">
                Comment passer commande ?
              </AccordionTrigger>
              <AccordionContent className="text-[#64748B] pb-5">
                Sélectionnez vos articles, indiquez votre adresse de livraison et vos dates de séjour. 
                Vous pouvez commander jusqu'à 3 jours avant votre arrivée (coupure quotidienne à 16h) pour la livraison gratuite. Le paiement s'effectue en ligne 
                de manière sécurisée.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-[#FAFAFA] rounded-xl border border-gray-100 px-6">
              <AccordionTrigger className="text-left font-['Playfair_Display'] font-medium text-[#2D3E50] hover:text-[#E8945A] py-5">
                Quels sont les délais de livraison ?
              </AccordionTrigger>
              <AccordionContent className="text-[#64748B] pb-5">
                Nous livrons le linge directement dans votre logement avant votre arrivée (généralement 
                prévue à 16h). Pour les arrivées anticipées, précisez-le lors de la commande.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-[#FAFAFA] rounded-xl border border-gray-100 px-6">
              <AccordionTrigger className="text-left font-['Playfair_Display'] font-medium text-[#2D3E50] hover:text-[#E8945A] py-5">
                Comment fonctionne la récupération du linge ?
              </AccordionTrigger>
              <AccordionContent className="text-[#64748B] pb-5">
                À la fin de votre séjour, laissez simplement le linge sur place. Nous nous chargeons 
                de le récupérer après votre départ. Aucune action de votre part n'est nécessaire.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-[#FAFAFA] rounded-xl border border-gray-100 px-6">
              <AccordionTrigger className="text-left font-['Playfair_Display'] font-medium text-[#2D3E50] hover:text-[#E8945A] py-5">
                Quelle est la qualité du linge ?
              </AccordionTrigger>
              <AccordionContent className="text-[#64748B] pb-5">
                Notre linge est en <strong>100 % polyester recyclé</strong> ou en <strong>coton 100 %</strong> selon les références.
                Draps en coloris <strong>gris ou vert</strong> (certaines pièces avec motifs). Linge de toilette
                (serviettes, tapis, torchons) en coloris <strong>gris, beige, bleu ou vert</strong>. Tout est lavé et repassé
                professionnellement selon des normes d'hygiène strictes (lavage haute température, contrôle qualité avant chaque livraison).
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-[#FAFAFA] rounded-xl border border-gray-100 px-6">
              <AccordionTrigger className="text-left font-['Playfair_Display'] font-medium text-[#2D3E50] hover:text-[#E8945A] py-5">
                Puis-je modifier ou annuler ma commande ?
              </AccordionTrigger>
              <AccordionContent className="text-[#64748B] pb-5">
                <strong>Non.</strong> En validant votre commande, vous demandez l'exécution immédiate du service et renoncez
                expressément à votre droit de rétractation (article L.221-28 du Code de la consommation, accepté via la case
                à cocher au paiement). <strong>Toute commande payée est ferme et définitive : aucun remboursement
                n'est possible</strong>, qu'il soit total ou partiel, en cas d'annulation de séjour, modification de date,
                non-utilisation du linge ou restitution anticipée. Détails à l'Article 22 des CGV.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
